import type { NylPilotMetricsResponse } from '@shared';
import { Button, Skeleton } from '@design-system';
import { RefreshCw } from 'lucide-react';
import { MetricCard } from './metric-card';
import { MetricsDateFilter, MetricsOrganizationFilter } from './metrics-date-filter';
import { MetricsOrganization, UseAdvisorMetricsResult } from '../types/metrics-type';

interface OverviewTabProps {
  metrics: UseAdvisorMetricsResult;
  onMetricClick: (tab: string, filterKey: string) => void;
  nylMetrics?: NylPilotMetricsResponse;
  nylAllTimeMetrics?: NylPilotMetricsResponse;
  showNylMetrics?: boolean;
  organization?: MetricsOrganization;
  onOrganizationChange?: (organization: MetricsOrganization) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function OverviewTab({
  metrics,
  onMetricClick,
  nylMetrics,
  nylAllTimeMetrics,
  showNylMetrics = false,
  organization,
  onOrganizationChange,
  onRefresh,
  isRefreshing = false,
}: OverviewTabProps) {
  const {
    data: { advisorMetrics, subscriptionMetrics, misc },
    actions: { setDatePreset, setCustomDates },
  } = metrics;
  const {
    totalAdvisors,
    wizardAdvisors,
    activeAccessAdvisors,
    advisorsWithoutActiveAccess,
    pendingSetupAdvisors,
  } = advisorMetrics;
  const totalNonWizardAdvisors = totalAdvisors - wizardAdvisors.length;
  const activeWizardAdvisors = activeAccessAdvisors.filter(advisor => advisor.isWizardAdvisor);
  const activeNonWizardAdvisors = activeAccessAdvisors.length - activeWizardAdvisors.length;
  const inactiveWizardAdvisors = advisorsWithoutActiveAccess.filter(
    advisor => advisor.isWizardAdvisor,
  );
  const inactiveNonWizardAdvisors =
    advisorsWithoutActiveAccess.length - inactiveWizardAdvisors.length;
  const {
    subscriptionDetails,
    activeSubscriptions,
    trialSubscriptions,
    totalInactiveSubscriptions,
    totalActiveSubscriptions,
    activeSubscriptionTotalSeats,
    totalUtilizedSeats,
    subscriptionUtilizationRate,
  } = subscriptionMetrics;
  const { dateFilter } = misc;
  const showAllTimeComparison = dateFilter.preset !== 'allTime';
  const advisorLogins = sumAdvisorLogins(nylMetrics);
  const allTimeAdvisorLogins = maxAllTimeMetric(advisorLogins, sumAdvisorLogins(nylAllTimeMetrics));
  const allTimeSelected = dateFilter.preset === 'allTime';
  const totalAdvisorsDescription = allTimeSelected
    ? 'The total number of advisors in the selected partner group. This includes both advisors who can currently use Waterlily and advisors who do not currently have access.'
    : 'Advisors added to the selected partner group during the selected date range. This includes both advisors who can currently use Waterlily and advisors who do not currently have access.';
  const totalSubscriptionsDescription = allTimeSelected
    ? 'All subscriptions in the selected partner group, including active, trial and inactive subscriptions.'
    : 'Subscriptions created in the selected partner group during the selected date range, including subscriptions that are currently active, in trial or inactive.';
  const activeTimeDescription = allTimeSelected
    ? 'The total recorded time NYL advisors actively used Waterlily since active-time tracking began. Time when the app was idle or not visible is not counted.'
    : 'The total time NYL advisors actively used Waterlily during the selected date range. Time when the app was idle or not visible is not counted.';
  const seatUtilizationDescription = `${
    allTimeSelected
      ? 'Shows the percentage of advisor seats from active and trial subscriptions that are currently in use.'
      : 'Shows the percentage of advisor seats currently in use among subscriptions created during the selected date range that are now active or in trial.'
  } ${totalUtilizedSeats} of ${activeSubscriptionTotalSeats} available ${activeSubscriptionTotalSeats === 1 ? 'seat' : 'seats'} ${totalUtilizedSeats === 1 ? 'is' : 'are'} in use across ${activeSubscriptions.length} active and ${trialSubscriptions.length} trial ${totalActiveSubscriptions.length === 1 ? 'subscription' : 'subscriptions'}. Because one subscription can include more than one seat, the seat count can be higher than the subscription count.`;
  const secondaryValue = (value: string | number): string | number | undefined => {
    if (!showAllTimeComparison) return undefined;
    return nylAllTimeMetrics ? value : '—';
  };
  const selectedPeriodUnavailable = !nylMetrics;
  const subscriptionCount = subscriptionDetails.length;
  const advisorById = new Map(
    advisorMetrics.advisorDetails.map(advisor => [advisor.advisorId, advisor]),
  );
  const pendingSetupAdvisorIds = new Set(pendingSetupAdvisors.map(advisor => advisor.advisorId));
  const activeNonWizardAdvisorIds = new Set(
    activeAccessAdvisors
      .filter(advisor => !advisor.isWizardAdvisor)
      .map(advisor => advisor.advisorId),
  );
  const utilizedSeatAdvisorIds = totalActiveSubscriptions.flatMap(subscription =>
    (subscription.seats ?? [])
      .filter(seat => seat.status === 'active' && seat.advisorId)
      .map(seat => seat.advisorId),
  );
  const utilizedWizardSeats = utilizedSeatAdvisorIds.filter(
    advisorId => advisorById.get(advisorId)?.isWizardAdvisor,
  ).length;
  const utilizedPendingSetupSeats = utilizedSeatAdvisorIds.filter(
    advisorId =>
      !advisorById.get(advisorId)?.isWizardAdvisor && pendingSetupAdvisorIds.has(advisorId),
  ).length;
  const utilizedActiveAdvisorSeats = utilizedSeatAdvisorIds.filter(advisorId =>
    activeNonWizardAdvisorIds.has(advisorId),
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex w-full flex-wrap items-end gap-3 sm:w-auto">
          {organization !== undefined && onOrganizationChange && (
            <MetricsOrganizationFilter
              organization={organization}
              onOrganizationChange={onOrganizationChange}
              idPrefix="overview-metrics"
            />
          )}
          <MetricsDateFilter
            dateFilter={dateFilter}
            onPresetChange={setDatePreset}
            onCustomDatesChange={setCustomDates}
          />
        </div>
        {onRefresh && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh overview metrics"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>

      {/* Advisors Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Advisors signups</h3>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Total advisors"
            value={totalAdvisors}
            isLoading={isRefreshing}
            supportingText={formatAdvisorTypes(wizardAdvisors.length, totalNonWizardAdvisors)}
            description={totalAdvisorsDescription}
            onClick={() => onMetricClick('advisors', 'all')}
          />
          <MetricCard
            label="Active advisors"
            value={activeAccessAdvisors.length}
            isLoading={isRefreshing}
            supportingText={formatAdvisorTypes(
              activeWizardAdvisors.length,
              activeNonWizardAdvisors,
            )}
            description="Advisors who can currently access Waterlily."
            onClick={() => onMetricClick('advisors', 'activeAccess')}
          />
          <MetricCard
            label="Pending setup"
            value={pendingSetupAdvisors.length}
            isLoading={isRefreshing}
            description="Advisors who have created an account but have not finished setting it up."
            onClick={() => onMetricClick('advisors', 'pendingSetup')}
          />
          <MetricCard
            label="Inactive advisors"
            value={advisorsWithoutActiveAccess.length}
            isLoading={isRefreshing}
            supportingText={formatAdvisorTypes(
              inactiveWizardAdvisors.length,
              inactiveNonWizardAdvisors,
            )}
            description="Advisors who cannot currently access Waterlily."
            onClick={() => onMetricClick('advisors', 'withoutActiveAccess')}
          />
        </div>
      </div>

      {/* Subscriptions Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Subscriptions</h3>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Total subscriptions"
            value={subscriptionCount}
            isLoading={isRefreshing}
            description={totalSubscriptionsDescription}
            onClick={() => onMetricClick('subscriptions', 'all')}
          />
          <MetricCard
            label="Active"
            value={activeSubscriptions.length}
            isLoading={isRefreshing}
            description={`${allTimeSelected ? 'Subscriptions' : 'Subscriptions created during the selected date range'} that are currently active and provide advisors access to Waterlily. Trial subscriptions are shown separately.`}
            onClick={() => onMetricClick('subscriptions', 'active')}
          />
          <MetricCard
            label="Trial"
            value={trialSubscriptions.length}
            isLoading={isRefreshing}
            description={`${allTimeSelected ? 'Subscriptions' : 'Subscriptions created during the selected date range'} that are still in their trial period. They currently provide access to Waterlily and their seats are included in Active Advisors and seat utilization.`}
            onClick={() => onMetricClick('subscriptions', 'trialing')}
          />
          <MetricCard
            label="Inactive"
            value={totalInactiveSubscriptions.length}
            isLoading={isRefreshing}
            description={`${allTimeSelected ? 'Subscriptions' : 'Subscriptions created during the selected date range'} that are canceled, paused, past due, unpaid or incomplete and do not currently provide advisors access to Waterlily.`}
            onClick={() => onMetricClick('subscriptions', 'inactive')}
          />
          <MetricCard
            label="Active seat utilization"
            value={`${subscriptionUtilizationRate}%`}
            isLoading={isRefreshing}
            supportingText={`${totalUtilizedSeats} of ${activeSubscriptionTotalSeats} ${activeSubscriptionTotalSeats === 1 ? 'seat' : 'seats'} utilized`}
            description={seatUtilizationDescription}
          />
        </div>
        {allTimeSelected &&
          (isRefreshing ? (
            <Skeleton
              className="mt-3 h-5 w-full max-w-3xl"
              data-testid="subscription-access-summary-loading"
            />
          ) : (
            <p
              className="mt-3 text-sm text-gray-600"
              data-testid="subscription-access-summary"
            >
              {subscriptionCount} {subscriptionCount === 1 ? 'subscription' : 'subscriptions'}{' '}
              total: {totalActiveSubscriptions.length} active (including trial) and{' '}
              {totalInactiveSubscriptions.length} inactive. The {totalActiveSubscriptions.length}{' '}
              active{' '}
              {totalActiveSubscriptions.length === 1 ? 'subscription has' : 'subscriptions have'}{' '}
              {activeSubscriptionTotalSeats} {activeSubscriptionTotalSeats === 1 ? 'seat' : 'seats'}
              . {totalUtilizedSeats === activeSubscriptionTotalSeats ? 'All ' : ''}
              {totalUtilizedSeats} {totalUtilizedSeats === 1 ? 'seat is' : 'seats are'} currently in
              use:{' '}
              {formatSeatAssignment(
                utilizedActiveAdvisorSeats,
                'an active advisor',
                'active advisors',
              )}
              ,{' '}
              {formatSeatAssignment(
                utilizedPendingSetupSeats,
                'an advisor pending setup',
                'advisors pending setup',
              )}
              , and{' '}
              {formatSeatAssignment(utilizedWizardSeats, 'a wizard account', 'wizard accounts')}.
            </p>
          ))}
      </div>

      {/* Activity Section */}
      {showNylMetrics && (
        <div>
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Activity</h3>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Advisor login sessions"
              value={selectedPeriodUnavailable ? '—' : advisorLogins}
              isLoading={isRefreshing}
              secondaryValue={secondaryValue(allTimeAdvisorLogins)}
              description="The total number of signed-in sessions started by advisors during the selected date range. Multiple sessions by the same advisor are counted separately."
            />
            {organization !== 'all' && (
              <MetricCard
                label="Total active time"
                isLoading={isRefreshing}
                value={
                  selectedPeriodUnavailable
                    ? '—'
                    : formatDuration(nylMetrics?.overview.activeSeconds)
                }
                secondaryValue={secondaryValue(
                  formatDuration(
                    maxAllTimeMetric(
                      nylMetrics?.overview.activeSeconds,
                      nylAllTimeMetrics?.overview.activeSeconds,
                    ),
                  ),
                )}
                description={activeTimeDescription}
              />
            )}
            <MetricCard
              label="Invitations sent"
              isLoading={isRefreshing}
              value={selectedPeriodUnavailable ? '—' : (nylMetrics?.overview.invitationsSent ?? 0)}
              secondaryValue={secondaryValue(
                maxAllTimeMetric(
                  nylMetrics?.overview.invitationsSent,
                  nylAllTimeMetrics?.overview.invitationsSent,
                ),
              )}
              description="The number of client invitations successfully sent during the selected date range."
            />
            <MetricCard
              label="Questionnaires completed"
              isLoading={isRefreshing}
              value={
                selectedPeriodUnavailable
                  ? '—'
                  : (nylMetrics?.overview.questionnairesCompleted ?? 0)
              }
              secondaryValue={secondaryValue(
                maxAllTimeMetric(
                  nylMetrics?.overview.questionnairesCompleted,
                  nylAllTimeMetrics?.overview.questionnairesCompleted,
                ),
              )}
              description="The number of clients who completed their first intake questionnaire during the selected date range."
            />
            <MetricCard
              label="Prediction summaries generated"
              isLoading={isRefreshing}
              value={
                selectedPeriodUnavailable
                  ? '—'
                  : (nylMetrics?.overview.planningOutputsGenerated ?? 0)
              }
              secondaryValue={secondaryValue(
                maxAllTimeMetric(
                  nylMetrics?.overview.planningOutputsGenerated,
                  nylAllTimeMetrics?.overview.planningOutputsGenerated,
                ),
              )}
              description="The number of LTC prediction summaries created or regenerated during the selected date range. Regenerating a summary counts again."
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return 'Not yet';
  const seconds = Math.max(0, Math.round(totalSeconds));
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);

  if (days > 0) return `${days}d${hours > 0 ? ` ${hours}h` : ''}`;
  if (hours > 0) return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
  return `${minutes}m`;
}

function sumAdvisorLogins(metrics: NylPilotMetricsResponse | undefined): number {
  return metrics?.agents.reduce((total, agent) => total + agent.logins, 0) ?? 0;
}

function maxAllTimeMetric(
  selectedPeriodValue: number | undefined,
  allTimeValue: number | undefined,
): number {
  return Math.max(selectedPeriodValue ?? 0, allTimeValue ?? 0);
}

function formatAdvisorTypes(wizards: number, advisors: number): string {
  return `${wizards} ${wizards === 1 ? 'wizard' : 'wizards'} · ${advisors} ${advisors === 1 ? 'advisor' : 'advisors'}`;
}

function formatSeatAssignment(count: number, singular: string, plural: string): string {
  return `${count} by ${count === 1 ? singular : plural}`;
}
